import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import PhiegLoader from "components/PhiegLoader";
import { api } from "state/api";

/**
 * The opening-the-CRM loading screen. Shows ONCE per page-load, when the app
 * shell first mounts: a solid brand veil with the Phieg "O" spinning; once the
 * first page's queries settle, the ring fills to a complete "O", "Phieg" types
 * in (the full, unhurried ceremony — this is the one brand moment per
 * session), then the veil fades out to reveal the app.
 *
 * It never comes back on navigation — in-page loading is each page's own
 * LoadingGate. Safe by construction: an overlay (the app mounts underneath)
 * with a hard max-timeout so it can never get stuck.
 */
const GRACE_MS = 400; // minimum time on screen before sealing may start
const SETTLE_MS = 250; // pending must hold at 0 this long before we seal
const SEAL_MS = 1250; // ring fill + "Phieg" types + a beat to land
const FADE_MS = 260; // veil fade-out
const MAX_MS = 6000; // hard cap — never let the splash stick

let bootShownThisPageLoad = false;

export default function BootSplash() {
  const pending = useSelector((s) => {
    const slice = s[api.reducerPath];
    if (!slice || !slice.queries) return 0;
    let n = 0;
    for (const k in slice.queries) {
      if (slice.queries[k] && slice.queries[k].status === "pending") n += 1;
    }
    return n;
  });

  // idle | loading | sealing | fading
  const [phase, setPhase] = useState(
    bootShownThisPageLoad ? "idle" : "loading"
  );
  const [armed, setArmed] = useState(false); // grace period elapsed
  const timers = useRef([]);

  const push = (t) => timers.current.push(t);

  useEffect(() => {
    // NOTE: do NOT early-return on bootShownThisPageLoad here. React StrictMode
    // (dev) mounts → unmounts (cleanup clears these timers) → re-mounts; an
    // early-return would skip re-arming them and the splash would stick forever.
    // The module flag still guards the *initial phase* (below) so a genuine
    // Layout remount doesn't replay the ceremony — but the safety timers must
    // always (re)arm on every mount so the veil can never get stuck.
    bootShownThisPageLoad = true;
    push(setTimeout(() => setArmed(true), GRACE_MS));
    push(
      setTimeout(() => {
        setPhase((p) => (p === "loading" ? "sealing" : p));
      }, MAX_MS)
    );
    const cleanup = timers.current;
    return () => cleanup.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seal once the boot queries settle (and the grace period has passed).
  useEffect(() => {
    if (phase !== "loading" || !armed || pending > 0) return undefined;
    const t = setTimeout(() => setPhase("sealing"), SETTLE_MS);
    return () => clearTimeout(t);
  }, [phase, armed, pending]);

  // Ceremony, then fade, then gone.
  useEffect(() => {
    if (phase === "sealing") {
      const t = setTimeout(() => setPhase("fading"), SEAL_MS);
      push(t);
      return () => clearTimeout(t);
    }
    if (phase === "fading") {
      const t = setTimeout(() => setPhase("idle"), FADE_MS);
      push(t);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [phase]);

  if (phase === "idle") return null;

  return (
    <Box
      data-testid="boot-splash"
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1500,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#1A2A27",
        opacity: phase === "fading" ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease`,
      }}
    >
      <PhiegLoader
        size={108}
        value={phase === "loading" ? undefined : 100}
        wordmark={phase !== "loading"}
      />
    </Box>
  );
}
